import moment, { Moment } from "moment-timezone";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { castPeriod, isValidPeriod, Periodable } from "@ct-react/core";
import { BookingConfig, RangePickerController } from "@ct-react/calendar";
import { usePeriodParams } from "../../hooks/query-params";
import { useTools } from "../../hooks/tools";
import "./wrap-booking-calendar.scss";

type Props = {
  config?: BookingConfig<Moment>;
  numberOfMonths: number;
  orientation?: "horizontal" | "vertical";
  resettableChange?: (resettable: boolean) => void;
};
export type WrapBookingCalendarHandler = { reset: () => void };

const WrapBookingCalendar = forwardRef<WrapBookingCalendarHandler, Props>((
  { config, numberOfMonths, orientation = "horizontal", resettableChange = () => void 0 }, forwardedRef) => {

  const today = moment.utc();
  const { labelizeCalendar } = useTools();
  const [ period, setPeriod ] = usePeriodParams();

  const [ range, setRange ] = useState<Periodable<Moment>>(!!period ? castPeriod(period) : {});
  const [ focus, setFocus ] = useState<null | "start" | "end">("start");

  const isDayBlocked = useCallback((day: Moment) => {

    const isBooked = config?.blocks.some(bd => bd.isSame(day, "day"));
    if (config?.starters.length === 0 ) return isBooked ? "booked" : true;
    if (!day.isBetween(config?.period.start, config?.period.end, "day", "(]")) return true;

    const arrivalRef = focus === "end" ? range.start! : day;
    const matchedArrival = config?.starters.find(arr => arr.startingAt.isSame(arrivalRef, "day"));
    const isArrivalOffset = !!matchedArrival && matchedArrival.startingAt.clone().subtract(matchedArrival.dayOffset, "day")
                                                       .isBefore(today, "day");

    if (focus !== "end") {
      if (isBooked) return "booked";
      if (arrivalRef.clone().add(1, "day").isSameOrAfter(config!.period.end, "day")) return true;
      if (!matchedArrival || matchedArrival.enders.every(dep => !dep.bookable)) return "online-restricted";
      return isArrivalOffset;
    }

    const limit = moment.min([
      config!.period.end,
      ...(config?.blocks || []).filter(bd => bd.isAfter(range.start!, "day"))
    ]);

    if (day.isAfter(range.start) && day.isSameOrBefore(limit, "day")) {
      const matchedDeparture = matchedArrival?.enders.find(dep => dep.endingAt.isSame(day, "day"));
      if (!!matchedDeparture && matchedDeparture.bookable) return false;
      return "online-restricted";
    }
    if (isBooked) return "booked";
    return true;

  }, [ config, range, focus, today ]);

  useImperativeHandle(forwardedRef, () => ({
    reset: () => {
      (focus === null) && setPeriod(undefined);
      if (focus === "end") {
        setRange({});
        setFocus("start");
      }
    }
  }));

  useEffect(() => {
    setRange(!!period ? castPeriod(period) : {});
    setFocus(!!period ? null : "start");
  }, [ period ]);

  useEffect(() => {
    resettableChange(focus !== "start");
  }, [ focus ]);

  const onRangeChange = useCallback((range: Periodable<Moment>) => {
    setRange(range);
    isValidPeriod(range) && setPeriod(range);
  }, []);

  return (
    <div className="booking-styles-calendar">
      <RangePickerController minDate={moment.max(today, config?.period.start ?? today)}
                             maxDate={config?.period.end}
                             numberOfMonths={numberOfMonths}
                             orientation={orientation}
                             firstDayOfWeek={1}
                             labelize={labelizeCalendar}
                             isDayBlocked={isDayBlocked}
                             selectedRange={range}
                             pickFocus={focus}
                             onSelectedRangeChange={onRangeChange}
                             onPickFocusChange={setFocus} />
    </div>);

});

export default WrapBookingCalendar;
